import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward, LocalShipping } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { RootState, GuestCheckoutData, Address, PaymentMethod, CartItem, ShippingAddress, CartItemDto } from '../../types';
import PriceDisplay from '../../components/common/PriceDisplay';
import { clearLocalCart } from '../../store/slices/cartSlice';
import api from '../../services/api';
import { DEFAULT_BGN_TO_EUR_RATE } from '../../utils/currency';
import analytics from '../../utils/analytics';

const GuestCheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const steps = [t('checkout.contactInfo'), t('checkout.shippingAddress'), t('checkout.paymentMethod'), t('checkout.reviewOrder')];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items, totalAmount } = useSelector((state: RootState) => state.cart);
  
  // Free shipping for orders over 150 EUR, otherwise calculated by ECONT
  // totalAmount is in BGN; convert to EUR before comparing to the EUR-native threshold
  const freeShippingThresholdEur = 150.00;
  const isFreeShipping = (totalAmount / DEFAULT_BGN_TO_EUR_RATE) >= freeShippingThresholdEur;
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<any>(null);
  const [completedItems, setCompletedItems] = useState<CartItem[]>([]);
  // Mirrors completedOrder, but as a ref so the empty-cart redirect guard
  // below can read it synchronously. Redux's cart update (dispatch(clearLocalCart()))
  // and this component's own state updates don't always land in the same
  // render, so a state-only guard can see items=[] before completedOrder
  // has committed and redirect to /cart right after a successful order.
  const orderCompletedRef = useRef(false);
  
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  
  const [shippingAddress, setShippingAddress] = useState<Address>({
    street: '',
    city: '',
    postalCode: '',
    country: 'Bulgaria',
  });
  
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH_ON_DELIVERY');
  const [notes, setNotes] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  }>({});

  useEffect(() => {
    if (items.length === 0 && !orderCompletedRef.current) {
      navigate('/cart');
    }
  }, [items, navigate]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) return 'This field is required';
        if (value.trim().length < 2) return 'Must be at least 2 characters';
        return undefined;
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        return undefined;
      case 'phone':
        if (!value.trim()) return 'Phone is required';
        if (value.trim().length < 10) return 'Phone must be at least 10 digits';
        return undefined;
      case 'street':
        if (!value.trim()) return 'Address is required';
        if (value.trim().length < 5) return 'Address must be at least 5 characters';
        return undefined;
      case 'city':
        if (!value.trim()) return 'City is required';
        if (value.trim().length < 2) return 'City must be at least 2 characters';
        return undefined;
      case 'postalCode':
        if (!value.trim()) return 'Postal code is required';
        return undefined;
      case 'country':
        if (!value.trim()) return 'Country is required';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateCurrentStep = (): boolean => {
    let newErrors: typeof errors = {};
    let isValid = true;

    switch (activeStep) {
      case 0: // Contact Information
        newErrors.firstName = validateField('firstName', guestInfo.firstName);
        newErrors.lastName = validateField('lastName', guestInfo.lastName);
        newErrors.email = validateField('email', guestInfo.email);
        newErrors.phone = validateField('phone', guestInfo.phone);
        isValid = !newErrors.firstName && !newErrors.lastName && !newErrors.email && !newErrors.phone;
        break;
      case 1: // Shipping Address
        newErrors.street = validateField('street', shippingAddress.street);
        newErrors.city = validateField('city', shippingAddress.city);
        newErrors.postalCode = validateField('postalCode', shippingAddress.postalCode);
        newErrors.country = validateField('country', shippingAddress.country);
        isValid = !newErrors.street && !newErrors.city && !newErrors.postalCode && !newErrors.country;
        break;
      case 2: // Payment Method
        isValid = true;
        break;
      default:
        isValid = true;
    }

    setErrors(newErrors);
    if (!isValid) {
      toast.error('Please fix the errors before continuing');
    }
    return isValid;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const checkoutData = {
        guest_name: `${guestInfo.firstName} ${guestInfo.lastName}`.trim(),
        guest_email: guestInfo.email,
        guest_phone: guestInfo.phone,
        shipping_address: {
          first_name: guestInfo.firstName,
          last_name: guestInfo.lastName,
          phone: guestInfo.phone,
          address_line_1: shippingAddress.street,
          city: shippingAddress.city,
          postal_code: shippingAddress.postalCode,
          country: shippingAddress.country
        },
        cart_items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          price: item.price
        })),
        payment_method: paymentMethod,
        notes,
      };

      
      const response = await api.post('/checkout/guest', checkoutData);

      if (response.data?.order) {
        const order = response.data.order;
        toast.success(t('checkout.messages.orderPlacedSuccess'));

        analytics.purchase(order, items);

        // Set synchronously, before dispatch(clearLocalCart()), so the
        // redirect-guard effect never sees items=[] without also seeing
        // this ref already flipped to true.
        orderCompletedRef.current = true;

        // Snapshot the items before clearing the cart so the confirmation
        // screen below still has something to display.
        setCompletedItems(items);
        setCompletedOrder(order);

        // Clear cart from both localStorage and Redux store
        dispatch(clearLocalCart());
      }
    } catch (error) {
      toast.error(t('checkout.messages.orderPlacedError'));
      console.error('Order placement error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('checkout.contactInfo')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('guestCheckout.fields.firstName')}
                value={guestInfo.firstName}
                onChange={(e) => setGuestInfo({ ...guestInfo, firstName: e.target.value })}
                onBlur={(e) => handleBlur('firstName', e.target.value)}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('guestCheckout.fields.lastName')}
                value={guestInfo.lastName}
                onChange={(e) => setGuestInfo({ ...guestInfo, lastName: e.target.value })}
                onBlur={(e) => handleBlur('lastName', e.target.value)}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.email')}
                type="email"
                value={guestInfo.email}
                onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                onBlur={(e) => handleBlur('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.phone')}
                value={guestInfo.phone}
                onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                onBlur={(e) => handleBlur('phone', e.target.value)}
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                required
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('checkout.shippingAddress')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.fields.street')}
                value={shippingAddress.street}
                onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                onBlur={(e) => handleBlur('street', e.target.value)}
                error={Boolean(errors.street)}
                helperText={errors.street}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.city')}
                value={shippingAddress.city}
                onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                onBlur={(e) => handleBlur('city', e.target.value)}
                error={Boolean(errors.city)}
                helperText={errors.city}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('checkout.fields.postalCode')}
                value={shippingAddress.postalCode}
                onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
                onBlur={(e) => handleBlur('postalCode', e.target.value)}
                error={Boolean(errors.postalCode)}
                helperText={errors.postalCode}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.fields.country')}
                value={shippingAddress.country}
                onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                onBlur={(e) => handleBlur('country', e.target.value)}
                error={Boolean(errors.country)}
                helperText={errors.country}
                required
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('checkout.paymentMethod')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ border: '2px solid', borderColor: 'primary.main', backgroundColor: 'action.hover' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalShipping sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" color="primary">
                        {t('checkout.cod.title')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('checkout.cod.subtitle')}
                      </Typography>
                    </Box>
                  </Box>
                  <Alert severity="success">
                    <Typography variant="body2">
                      ✓ {t('checkout.cod.benefit1')}<br />
                      ✓ {t('checkout.cod.benefit2')}<br />
                      ✓ {t('checkout.cod.benefit3')}
                    </Typography>
                  </Alert>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    {t('checkout.cod.note')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.orderNotes')}
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('checkout.orderNotesPlaceholder')}
              />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('checkout.orderSummary')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('guestCheckout.items')} ({items.length})
                  </Typography>
                  {items.map((item: CartItem) => (
                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{item.product.title} x {item.quantity}</Typography>
                      <PriceDisplay
                        price={item.price * item.quantity}
                        variant="body2"
                      />
                    </Box>
                  ))}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('cart.subtotal')}:</Typography>
                    <PriceDisplay price={totalAmount} variant="body2" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('cart.shipping')}:</Typography>
                    {isFreeShipping ? (
                      <Typography color="success.main">{t('guestCheckout.free')}</Typography>
                    ) : (
                      <Typography>{t('cart.econtTariffs')}</Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{t('cart.total')}:</Typography>
                    <PriceDisplay 
                      price={totalAmount} 
                      variant="h6" 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('guestCheckout.deliveryDetails')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>{guestInfo.firstName} {guestInfo.lastName}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {guestInfo.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {guestInfo.phone}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {shippingAddress.street}<br />
                    {shippingAddress.city} {shippingAddress.postalCode}<br />
                    {shippingAddress.country}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('guestCheckout.payment')}:</strong> {t('checkout.cod.title')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (completedOrder) {
    const orderNumber = completedOrder.orderNumber || completedOrder.order_number;
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="success.main">
            {t('checkout.messages.orderPlacedSuccess')}
          </Typography>
          {orderNumber && (
            <Typography variant="h6" gutterBottom>
              {t('checkout.orderNumber')}: {orderNumber}
            </Typography>
          )}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ textAlign: 'left' }}>
            {completedItems.map((item) => (
              <Box key={item.product.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {item.product.title} × {item.quantity}
                </Typography>
                <PriceDisplay price={item.price * item.quantity} />
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 3 }} />
          <Button variant="contained" onClick={() => navigate('/shop')}>
            {t('checkout.continueShopping')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/cart')}
          sx={{ mb: 2 }}
        >
          {t('checkout.messages.back')}
        </Button>
        <Typography variant="h4" gutterBottom>
          {t('checkout.guestCheckout')}
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('checkout.guestInfo')}
        </Alert>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            {t('checkout.messages.back')}
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handlePlaceOrder}
              disabled={loading}
              size="large"
            >
              {loading ? t('checkout.messages.processing') : t('checkout.placeOrder')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
            >
              {t('checkout.messages.next')}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestCheckoutPage;